import React, { useCallback } from 'react';
import styled from 'styled-components';
import { isEqual } from 'lodash-es';
import { useAppDispatch, useAppSelector } from '@/store';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { getStateValue } from '@/page-components/utils/getStateValue';
import {
  claimBonus,
  freeBetRemove,
  freeBetsFetch,
  freeSpinsFetch,
  getBonusAvailable,
  requestRemoveBonus,
  requestWallet,
} from '@/modules/casino/store/actions';
import {
  pokerTournamentsByTournamentTypesFetch,
  pokerTrTFetch,
} from '@/modules/casino/store/actions/poker_tournament_tickets';
import {
  claimPrizeById,
  claimPrizeByIdLoaded,
  loadPrizes,
  resetClaimPrizeError,
} from '@/modules/tournaments-missions/store/actions/history';
import { forfeitMomentumBonus, getMomentumBonuses } from '@/modules/momentum/store/actions/momentum';
import * as actionTypes from '@/modules/casino/store/actions/actionTypes.js';
import { handleActions } from '@/components/action-handler/actions';
import { clearCache } from '@//utils/axios_cache';

import './index.scss';
import { FeatureCategory, FeatureSubcategory, logEvent } from '@/analytics';
import { FeatureLocation } from '@/analytics/types/eventData';

type WalletBonusesProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    dsType: string;
  };
};

type PokerTrTDetails = {
  open: boolean;
  trt: string;
  // tournaments: any[];
};

const defaultProps = {
  className: '',
  styleText: '',
  properties: {
    dsType: '',
  },
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const WalletBonuses = (componentProps: WalletBonusesProps) => {
  const tmpProps = { ...defaultProps, ...componentProps };
  delete tmpProps.children;
  const props = JSON.parse(JSON.stringify(tmpProps));
  const { children } = componentProps;

  const dispatch = useAppDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  let tab = new URLSearchParams(location.search).get('tab') ?? 'bonus';
  if (!(tab === 'bonus' || tab === 'freespin' || tab === 'freebet' || tab === 'prize')) {
    tab = 'bonus';
  }

  const [selectedTab, setSelectedTab] = React.useState(tab);

  const {
    activeBonuses,
    pendingBonuses,
    availableBonuses,
    pendingBonusesLoader,
    bonusClaimError,
    activeBonusesLoader,
    loadingClaimBonus,
    prizes,
    loadingPrizes,
    claimPrizeByIdError,
    pokerTrTickets,
    pokerTournaments,
    loadingPokerTrTickets,
    pokerTrTLoaded,
  } = useAppSelector<{
    activeBonuses: any[];
    pendingBonuses: any[];
    availableBonuses: any | undefined | null;
    pendingBonusesLoader: any;
    activeBonusesLoader: any;
    bonusClaimError: any;
    loadingClaimBonus: boolean;
    prizes: any[];
    loadingPrizes: boolean;
    claimPrizeByIdError: boolean;
    pokerTrTickets: any[];
    pokerTournaments: any[];
    loadingPokerTrTickets: boolean;
    pokerTrTLoaded: boolean;
  }>(
    (state) => ({
      activeBonuses: getStateValue(state, 'state.activeBonuses'),
      pendingBonuses: getStateValue(state, 'state.pendingBonuses'),
      availableBonuses: getStateValue(state, 'state.application.availableBonuses'),
      pendingBonusesLoader: getStateValue(state, 'state.pendingBonuses.loader'),
      activeBonusesLoader: getStateValue(state, 'state.activeBonuses.loader'),
      bonusClaimError: state.application.bonusClaimError,
      loadingClaimBonus: state.application.loadingClaimBonus,
      prizes: state.tournamentsMissions.history.prizes,
      loadingPrizes: state.tournamentsMissions.history.loadingPrizes,
      claimPrizeByIdError: state.tournamentsMissions.history.claimPrizeByIdError,
      pokerTrTickets: state.poker.tickets,
      pokerTournaments: state.poker.tournaments,
      loadingPokerTrTickets: state.poker.loading,
      pokerTrTLoaded: state.poker.loaded,
    }),
    isEqual,
  );

  const momentumPrize = useAppSelector<any>((state) => state.momentum.prize.value);
  const momentumLoaded = useAppSelector((state) => state.momentum.prize.loaded);
  const authentication = useAppSelector((state) => state.authentication);

  const [disableButtons, setDisableButtons] = React.useState(true);

  React.useEffect(() => {
    dispatch(getBonusAvailable());
    dispatch(loadPrizes(null));
    dispatch(requestWallet());
    dispatch(freeSpinsFetch());
    dispatch(freeBetsFetch());
    setDisableButtons(false);
    if (!momentumLoaded && ['user', 'token'].indexOf(authentication.auth_type) > -1) {
      dispatch(getMomentumBonuses());
    }
    if (window?.config?.poker?.enablePokerTrT === '1') {
      dispatch(pokerTrTFetch());
    }
  }, [authentication.auth_type, dispatch, momentumLoaded]);

  const [isDialogOpen, setIsDialogOpen] = React.useState({
    open: false,
    deleteId: '',
    deleteType: '',
  });

  const [isActivateDialogOpen, setIsActivateDialogOpen] = React.useState(false);

  const getElementToActivate = useCallback(
    (e: any) => {
      if (!e) return;
      e.preventDefault();
      e.stopPropagation();

      const bonus = e.target.closest('.activate-pending-bonus-action-button');

      if (bonus && bonus.dataset) {
        // 1st reset any claim bonus or prize status
        dispatch({
          type: actionTypes.application.RESET_BONUS_CLAIM_STATUS,
        });
        dispatch(resetClaimPrizeError());

        // console.log('bonus.dataset', bonus.dataset);

        setIsActivateDialogOpen(true);
        switch (bonus?.dataset?.type.toString()) {
          case 'bonus':
            // console.log('DISPATCH CLAIM BONUS', bonus.dataset.bonusid);
            dispatch(claimBonus(bonus.dataset.bonusid, true));
            break;
          case 'prize':
          case 'prizeBundle':
            // console.log('DISPATCH CLAIM PRIZE', bonus.dataset.bonusid);
            dispatch(claimPrizeById({ id: bonus.dataset.bonusid }));
            break;
          // TODO: add more cases if needed
          default:
            console.error('Unknown delete type', bonus);
        }
      } else {
        setIsActivateDialogOpen(false);
        dispatch(claimPrizeByIdLoaded());
        // reset any claim bonus or prize status
        dispatch({
          type: actionTypes.application.RESET_BONUS_CLAIM_STATUS,
        });
        dispatch(resetClaimPrizeError());
        clearCache();
      }
    },
    [dispatch, setIsActivateDialogOpen],
  );

  const onPlayBonus = useCallback((e: any) => {
    if (!e) return;
    e.preventDefault();
    e.stopPropagation();

    const bonus = e.target.closest('.activate-pending-bonus-action-button');

    if (bonus && bonus.dataset) {
      /*
      "5-5": "free_bet_lotto",
      "5-9": "free_bet_sport",
      "8-8": "free_spin",
      "1-1": "bonus_scalar",
      "1-2": "bonus_percentage",
      "1-10": "bonus_after_wager",
      "8-81": "free_spin_timer",
      "8-82": "free_spin_bonus_round",
    */
      // console.log('PLAY BONUS', bonus.dataset, bonus.dataset.bonusid, bonus.dataset.type);
      if (bonus.dataset.type) {
        switch (bonus.dataset.type.toString()) {
          case '1':
          case '2':
          case '10':
            navigate('/casino');
            break;
          case '8':
          case '81':
          case '82':
            // launch game with bonus
            handleActions(
              'launch-slot-game',
              { properties: { 'data-id': bonus.dataset.id, 'data-name': bonus.dataset.name } },
              navigate,
              {},
              e,
            );
            break;
          case '5':
            console.log('LOTTO');
            navigate('/lotto');
            break;
          case '9':
            console.log('SPORT');
            navigate('/sport');
            break;
          default:
            console.log('DEF CASINO');
            navigate('/casino');
        }
      }
    }
  }, []);

  const getElementToRemove = useCallback((e: any) => {
    if (!e) return;
    e.preventDefault();

    // const testElement = getDOMElementByClassName('delete-active-bonus-action-button');
    const bonus = e.target.closest('.delete-active-bonus-action-button');
    // console.log('DEBUG RUNNING getElementToRemove', { bonus });
    // @ts-ignore
    if (bonus && bonus.dataset) {
      // @ts-ignore
      setIsDialogOpen((ps) => ({
        ...ps,
        open: !ps.open,
        deleteId: bonus.dataset.bonusid,
        deleteType: bonus.dataset.type,
      }));
    } else {
      setIsDialogOpen((ps) => ({ ...ps, open: false, deleteId: '', deleteType: '' }));
    }
  }, []);

  const onDeleteBonus = useCallback(
    (e: any) => {
      e.preventDefault();
      if (isDialogOpen.deleteType && isDialogOpen.deleteId) {
        setDisableButtons(true);
        switch (isDialogOpen.deleteType.toString()) {
          case '2':
            // console.log('DISPATCH REMOVE BONUS', isDialogOpen);
            dispatch(requestRemoveBonus(isDialogOpen.deleteId));
            break;
          case '9':
          case '5':
            // console.log('DISPATCH REMOVE FREEBET', isDialogOpen);
            dispatch(freeBetRemove(isDialogOpen.deleteId));
            break;
          case '1000':
            // console.log('delete bonus', isDialogOpen.deleteId);
            dispatch(forfeitMomentumBonus(isDialogOpen.deleteId ?? 0));
            break;
          // TODO: add more cases if needed
          default:
            console.error('Unknown delete type', isDialogOpen.deleteType);
          // setLoading(true);
        }
      }

      logEvent(
        e,
        {
          feature_used: 'wallet_bonus_delete',
          feature_category: FeatureCategory.Rewards,
          feature_subcategory: FeatureSubcategory.NotApplicable,
          feature_location: FeatureLocation.NotApplicable,
        },
        {
          bonus_id: isDialogOpen.deleteId,
          bonus_type: isDialogOpen.deleteType,
        },
      );

      setIsDialogOpen((ps) => ({ ...ps, open: false, deleteId: '', deleteType: '' }));
    },
    [dispatch, isDialogOpen.deleteId, isDialogOpen.deleteType, setDisableButtons],
  );

  React.useEffect(() => {
    setDisableButtons(false);
  }, [activeBonusesLoader.loading]);

  const contextValue = React.useMemo(() => {
    let ab = activeBonuses;
    if (momentumPrize?.active) {
      let prizeExpired = false;
      if (momentumPrize?.bonus_expires_at) {
        const now = new Date().valueOf();
        const prizeExpiresAt = new Date(momentumPrize?.bonus_expires_at).valueOf();
        if (now - prizeExpiresAt > 0) prizeExpired = true;
      }
      if (!prizeExpired) {
        let wager_target = momentumPrize?.wager_target ?? 0;
        wager_target = wager_target / 100;
        let progress = momentumPrize?.wager_progress ?? 0;

        progress = (progress * wager_target) / 100;
        progress = parseFloat(progress.toFixed(2));

        if (progress > wager_target) progress = wager_target;

        let value = (momentumPrize?.reward?.value ?? 0) / 100;
        if (
          momentumPrize?.lucky_star_prize?.reward_type === 'REWARD_INCREASE' &&
          momentumPrize?.lucky_star_prize?.reward_value
        ) {
          value += momentumPrize?.lucky_star_prize?.reward_value;
        }

        ab = [
          {
            type: 'bonus',
            bonus: {
              element_type: 'inner',
              data: {
                logo: 'https://micros-t.b-cdn.net/turbo-wins-1715922446297.png',
                id: momentumPrize?.uuid,
                eligibleProducts: [],
                amountGranted: value,
                amount: value,
                name: momentumPrize?.display_name,
                wager: progress,
                wagerTarget: momentumPrize?.wager_target / 100,
                startDate: new Date(momentumPrize?.created_at).valueOf() / 1000,
                endDate: new Date(momentumPrize?.bonus_expires_at).valueOf() / 1000,
                type: 1000,
                cms_data: {
                  terms_url: '/promotions/7f712d82-831d-48c7-b6e8-bb2a03afa536',
                },
                delete_action: 'activeBonuses.deleteBonus',
              },
            },
          },
          ...ab,
        ];
      }
    }

    const filterBonuses = (bonuses: any[]) => {
      const allBonuses: any[] = [];
      const allPrizes: any[] = [];
      const allFreeSpins: any[] = [];
      const allFreeBets: any[] = [];

      if (Array.isArray(bonuses) && bonuses.length > 0) {
        for (const b of bonuses) {
          // pending bonuses:
          if (b.type === 'bonus') {
            switch (b.bonus?.data?.bonus_available_type?.toString?.()) {
              case '2':
                allBonuses.push(b);
                break;
              case '9':
              case '5':
                allFreeBets.push(b);
                break;
              case '1000':
                // console.log('momentum bonus', b);
                allBonuses.push(b);
                break;
              case '8':
                allFreeSpins.push(b);
                break;
              default:
                allBonuses.push(b);
            }
          } else if (b.type === 'prize') {
            /*
          bonusRewardTypes = [
            { id: 'BONUS_FUNDS', name: 'BONUS_FUNDS' },
            { id: 'SPORT_FREE_BET', name: 'SPORT_FREE_BET' },
            { id: 'LOTO_FREE_BET', name: 'LOTO_FREE_BET' },
            { id: 'FREE_SPINS', name: 'FREE_SPINS' },
            { id: 'GOLDEN_CHIP', name: 'GOLDEN_CHIP' },
          ];
          */

            allPrizes.push(b);
            // TODO: uncomment when we have front design for each prize type
            /*
            console.log('DEBUG PRIZE TYPE', { b, type: b.prize?.data?.reward_type });
            switch (b.prize?.data?.reward_type) {
              case 'BONUS_FUNDS':
                allPrizes.push(b);
                break;
              case 'FREE_SPINS':
                allFreeSpins.push(b);
                break;
              case 'FREE_BET':
              case 'SPORT_FREE_BET':
                allFreeBets.push(b);
                break;
              // add more cases here
              default:
                // console.log('Default prize type', b.data?.reward_type);
                allPrizes.push(b);
            }*/
          }
          // active bonuses:
          else if (b.type === 'freebet') {
            allFreeBets.push(b);
          } else if (b.type === 'freespin') {
            allFreeSpins.push(b);
          }
        }
      }
      return {
        allBonuses,
        allPrizes,
        allFreeSpins,
        allFreeBets,
      };
    };

    const onOpenPokerTrTCard = (e: any) => {
      if (!e) return;
      // e.preventDefault();
      // e.stopPropagation();

      const trt = e?.currentTarget?.dataset?.refid ?? null;
      if (!trt) return;

      const ticket = pokerTrTickets.find((t) => t.referenceId.toString() === trt.toString());

      if (!ticket) {
        console.warn('fetchPokerTournaments no ticket found for ref id:', { trt, pokerTrTickets });
        return;
      }

      if (!pokerTournaments[trt]) {
        dispatch(pokerTournamentsByTournamentTypesFetch(trt, ticket.tournamentTypes));
      }
    };

    return {
      activeBonuses: ab,
      pendingBonuses,
      availableBonuses,
      isDialogOpen: isDialogOpen.open,
      deleteId: isDialogOpen.deleteId,
      deleteType: isDialogOpen.deleteType,
      toggleDialog: getElementToRemove,
      onDeleteBonus,
      bonusClaimError: bonusClaimError, //|| claimPrizeByIdError,

      // isActivateDialogOpen: isActivateDialogOpen && !loadingClaimBonus,
      isActivateDialogOpen: isActivateDialogOpen,
      toggleActivateDialog: getElementToActivate,
      loading: loadingClaimBonus || loadingPrizes,
      pendingBonusesLoading: pendingBonusesLoader.loading,
      activeBonusesLoading: activeBonusesLoader.loading,
      disableRemoveBonus: disableButtons || activeBonusesLoader.loading,

      // prizes,
      loadingPrizes,
      claimPrizeByIdError,

      allActiveBonuses: [...filterBonuses(ab).allBonuses],
      allActivePrizes: [...filterBonuses(ab).allPrizes],
      allActiveFreeSpins: [...filterBonuses(ab).allFreeSpins],
      allActiveFreeBets: [...filterBonuses(ab).allFreeBets],

      allPendingBonuses: [...filterBonuses(pendingBonuses).allBonuses],
      allPendingPrizes: [...filterBonuses(pendingBonuses).allPrizes],
      allPendingFreeSpins: [...filterBonuses(pendingBonuses).allFreeSpins],
      allPendingFreeBets: [...filterBonuses(pendingBonuses).allFreeBets],

      // Poker Tournament Tickets
      allPokerTrTickets: pokerTrTickets,
      onOpenPokerTrTCard,
      loadingPokerTrTickets,
      pokerTrTDetails: pokerTournaments,
      selectedTab: selectedTab,
      onTabChange: (selectedLayout: string) => {
        setSelectedTab(selectedLayout);
      },
      onPlayBonus,
    };
  }, [
    momentumPrize,
    activeBonuses,
    pendingBonuses,
    availableBonuses,
    isDialogOpen,
    onDeleteBonus,
    bonusClaimError,
    isActivateDialogOpen,
    getElementToActivate,
    getElementToRemove,
    loadingClaimBonus,
    pendingBonusesLoader.loading,
    activeBonusesLoader.loading,
    disableButtons,
    loadingPrizes,
    claimPrizeByIdError,
    selectedTab,
    onPlayBonus,
    pokerTrTickets,
    pokerTournaments,
    loadingPokerTrTickets,
    pokerTrTLoaded,
  ]);

  // console.log('WalletBonuses', {
  //   contextValue,
  //   prizes,
  //   pendingBonuses,
  //   activeBonuses,
  //   selectedTab,
  // });
  // console.log('WalletBonuses[contextValue]', contextValue);

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default WalletBonuses;
